import { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Printer, FileText, FileDown } from 'lucide-react';
import api from '../../../../services/api';
import {
  contratoService,
  Contrato,
  substituirVariaveis,
  formatarMoeda,
  formatarData,
  getStatusColor,
  getStatusLabel
} from '../../../../services/contratoService';

interface ContratoPreviewProps {
  contratoId: string;
  onBack: () => void;
}

export function ContratoPreview({ contratoId, onBack }: ContratoPreviewProps) {
  const [contrato, setContrato] = useState<Contrato | null>(null);
  const [loading, setLoading] = useState(true);
  const [empresa, setEmpresa] = useState<{ nomeFantasia?: string; razaoSocial?: string; cnpj?: string; cidade?: string } | null>(null);
  const printRef = useRef<HTMLDivElement>(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  useEffect(() => {
    carregarContrato();
    carregarEmpresa();
  }, [contratoId]);

  async function carregarEmpresa() {
    try {
      const res = await api.get('/admin/empresa');
      setEmpresa(res.data);
    } catch { }
  }

  async function carregarContrato() {
    try {
      setLoading(true);
      const data = await contratoService.getById(contratoId);
      setContrato(data);
    } catch (error) {
      console.error('Erro ao carregar contrato:', error);
      alert('Erro ao carregar contrato');
    } finally {
      setLoading(false);
    }
  }

  function handlePrint() {
    window.print();
  }

  async function handleDownloadPdf() {
    if (!contrato) return;
    try {
      setGeneratingPdf(true);
      await contratoService.downloadPdf(contrato.id, contrato.titulo);
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      alert('Erro ao gerar PDF');
    } finally {
      setGeneratingPdf(false);
    }
  }

  function getConteudoProcessado(): string {
    if (!contrato) return '';
    if (!contrato.conteudo) return '<p>Conteúdo do contrato não disponível.</p>';

    return substituirVariaveis(
      contrato.conteudo,
      {
        razaoSocial: contrato.client.razaoSocial,
        nomeFantasia: contrato.client.nomeFantasia,
        cnpj: contrato.client.cnpj || '',
      },
      {
        valorMensal: contrato.valorMensal != null ? Number(contrato.valorMensal) : null,
        dataInicio: contrato.dataInicio,
        dataFim: contrato.dataFim,
        diaVencimento: contrato.diaVencimento,
        numero: contrato.numero,
      },
      empresa
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!contrato) {
    return (
      <div className="text-center py-12">
        <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">Contrato não encontrado</h3>
        <button onClick={onBack} className="text-primary hover:underline">
          Voltar
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header - não aparece na impressão */}
      <div className="flex items-center justify-between print:hidden">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 hover:bg-muted rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-2xl font-bold text-foreground">Visualizar Contrato</h2>
            <p className="text-muted-foreground">{contrato.titulo}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            <Printer className="w-4 h-4" />
            Imprimir
          </button>
          <button
            type="button"
            onClick={handleDownloadPdf}
            disabled={generatingPdf}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
          >
            <FileDown className="w-4 h-4" />
            {generatingPdf ? 'Gerando...' : 'Gerar PDF'}
          </button>
        </div>
      </div>

      {/* Informações do contrato - não aparece na impressão */}
      <div className="bg-card rounded-lg shadow-sm p-6 print:hidden">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-muted-foreground">Cliente</p>
            <p className="font-medium">{contrato.client.nomeFantasia || contrato.client.razaoSocial}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Status</p>
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(contrato.status)}`}>
              {getStatusLabel(contrato.status)}
            </span>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Valor Mensal</p>
            <p className="font-medium">{contrato.valorMensal != null ? formatarMoeda(Number(contrato.valorMensal)) : 'A definir'}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Vencimento</p>
            <p className="font-medium">Dia {contrato.diaVencimento}</p>
          </div>
        </div>
      </div>

      {/* Conteúdo do contrato - área de impressão */}
      <div
        ref={printRef}
        className="bg-white rounded-lg shadow-sm p-8 print:shadow-none print:p-0"
        style={{ color: '#1a1a1a' }}
      >
        {/* Cabeçalho para impressão */}
        <div className="hidden print:block mb-8 pb-4 border-b">
          <h1 className="text-2xl font-bold text-center">{contrato.titulo}</h1>
          <div className="flex justify-between mt-4 text-sm text-muted-foreground">
            <span>Cliente: {contrato.client.nomeFantasia || contrato.client.razaoSocial}</span>
            <span>Data: {formatarData(new Date().toISOString())}</span>
          </div>
        </div>

        {/* Corpo do contrato */}
        <div
          className="contract-body"
          dangerouslySetInnerHTML={{ __html: getConteudoProcessado() }}
        />

        {/* Rodapé para impressão */}
        <div className="hidden print:block mt-16 pt-8 border-t">
          <div className="grid grid-cols-2 gap-8">
            <div className="text-center">
              <div className="border-t border-gray-400 pt-2 mt-16">
                <p className="text-sm">Contratante</p>
                <p className="text-xs text-muted-foreground">{contrato.client.nomeFantasia || contrato.client.razaoSocial}</p>
              </div>
            </div>
            <div className="text-center">
              <div className="border-t border-gray-400 pt-2 mt-16">
                <p className="text-sm">Contratada</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Estilos de impressão */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print\\:block,
          .print\\:block * {
            visibility: visible;
          }
          .print\\:hidden {
            display: none !important;
          }
          @page {
            margin: 2cm;
          }
        }
      `}</style>
    </div>
  );
}
